import { DocumentNode, getOperationAST, GraphQLError, parse, visit } from 'graphql';
import { ClientError, GraphQLResponse, RequestDocument } from 'graphql-request';
import { UNKNOWN_ERROR_MESSAGE } from '../hooks/useHandleError';

export const safeDocumentParse = (query: RequestDocument): DocumentNode | null => {
  try {
    return (typeof query === 'string' ? parse(query) : query) as DocumentNode;
  } catch {
    return null;
  }
};

export const getOperationName = (query: string | DocumentNode): string | undefined => {
  const parsedDoc = safeDocumentParse(query);
  const firstSelection = parsedDoc && getOperationAST(parsedDoc)?.selectionSet.selections[0];
  return firstSelection?.kind === 'Field' ? firstSelection.name.value : undefined;
};

export const getVariableName = (document: RequestDocument, index: number = 0): string | undefined => {
  const parsedDoc = safeDocumentParse(document);
  const operation = parsedDoc && getOperationAST(parsedDoc);

  if (operation?.kind === 'OperationDefinition') {
    return operation.variableDefinitions?.[index]?.variable.name.value;
  }

  return undefined;
};

export const getGraphQLError = (error: ClientError) => error?.response?.errors?.[0];

export const extractVariableTypes = (document: DocumentNode): Record<string, string> => {
  const variableTypes = {};

  visit(document, {
    VariableDefinition(node) {
      const variableName = node.variable.name.value;
      const variableType = node.type;

      if (variableType.kind === 'NamedType') {
        variableTypes[variableName] = variableType.name.value;
      } else if (variableType.kind === 'NonNullType' && variableType.type.kind === 'NamedType') {
        variableTypes[variableName] = variableType.type.name.value;
      }
    },
  });

  return variableTypes;
};

export const coerceVariablesWithTypes = (variables: Record<string, any>, variableTypes: Record<string, string>) => {
  const coerceValue = (value: any, type: string) => {
    if (type === 'Int' && typeof value === 'string' && /^\d+$/.test(value)) {
      return parseInt(value);
    }

    if (type === 'Float' && typeof value === 'string' && !Number.isNaN(value)) {
      return parseFloat(value);
    }

    if (type === 'Boolean') {
      return value === 'false' ? false : !!value;
    }

    return value;
  };

  return Object.entries(variables).reduce((acc, [key, value]) => {
    const expectedType = variableTypes[key];
    acc[key] = coerceValue(value, expectedType);
    return acc;
  }, {});
};

export function isGraphQLResponse<T>(response: unknown): response is GraphQLResponse<T> {
  return typeof response === 'object' && response !== null && 'data' in response;
}

export function isClientError(response: unknown): response is ClientError {
  return response instanceof ClientError;
}

export function isGenericError(response: unknown): response is Error {
  return response instanceof Error && !isClientError(response);
}

export const extractErrorMessage = (error: unknown): string => {
  // eslint-disable-next-line fabric-ui/no-let
  let message: string | undefined;
  if (isClientError(error)) {
    message = getGraphQLError(error)?.message;
  } else if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  }

  return message || UNKNOWN_ERROR_MESSAGE;
};

export const convertToGraphQLError = (error: unknown): GraphQLError => {
  if (error instanceof ClientError) {
    const graphQLError = getGraphQLError(error);
    if (graphQLError) {
      return graphQLError;
    }
  }
  if (error instanceof Error) {
    return new GraphQLError(error.message, { originalError: error });
  }
  return new GraphQLError('An unknown error occurred', { originalError: error as Error | undefined });
};
